import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatToLocalDateTime(date: Date) {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

export function formatDateToDateOnly(date: Date) {
  const pad = (num: number) => num.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
}

export function parseDurationToSeconds(input: string): number | null {
  // Regex para HH:MM:SS
  const hhmmssRegex = /^(\d+):(\d{2}):(\d{2})$/;
  const matchHHMMSS = input.match(hhmmssRegex);
  if (matchHHMMSS) {
    const hours = parseInt(matchHHMMSS[1], 10);
    const minutes = parseInt(matchHHMMSS[2], 10);
    const seconds = parseInt(matchHHMMSS[3], 10);
    return hours * 3600 + minutes * 60 + seconds;
  }

  // Regex para lidar com "X dias", "X horas", "X minutos", "XhYm" etc.
  // Encontra todos os pares de número e unidade
  const partRegex = /(\d+)\s*(d|dia|dias|h|hora|horas|m|min|minuto|minutos|s|seg|segundo|segundos)/gi;
  let totalSeconds = 0;
  let matched = false;

  let match;
  while ((match = partRegex.exec(input)) !== null) {
    matched = true;
    const value = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();

    if (unit.startsWith('d')) {
      totalSeconds += value * 86400;
    } else if (unit.startsWith('h')) {
      totalSeconds += value * 3600;
    } else if (unit.startsWith('m')) {
      totalSeconds += value * 60;
    } else if (unit.startsWith('s')) {
      totalSeconds += value;
    }
  }

  return matched && totalSeconds > 0 ? totalSeconds : null;
}

export function secondsToDurationString(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (num: number) => num.toString().padStart(2, '0');

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export function formatDurationLabel(durationString: string): string {
  let totalSeconds = 0;

  // Handle format "X HH:MM:SS" where X is days
  const dayTimeRegex = /^(\d+)\s+(\d{2}):(\d{2}):(\d{2})$/;
  const dayMatch = durationString.match(dayTimeRegex);
  
  if (dayMatch) {
    const days = parseInt(dayMatch[1], 10);
    const hours = parseInt(dayMatch[2], 10);
    const minutes = parseInt(dayMatch[3], 10);
    const seconds = parseInt(dayMatch[4], 10);
    totalSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;
  } else {
    // Handle format "HH:MM:SS"
    const parts = durationString.split(':').map(Number);
    if (parts.length !== 3) {
      return durationString; // Retorna a string original se não for HH:MM:SS
    }
    const [hours, minutes, seconds] = parts;
    totalSeconds = hours * 3600 + minutes * 60 + seconds;
  }

  if (totalSeconds === 0) {
    return 'No reminder';
  }

  const days = Math.floor(totalSeconds / 86400);
  const remainingHours = Math.floor((totalSeconds % 86400) / 3600);
  const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);

  let label = '';
  if (days > 0) {
    label += `${days} dia${days > 1 ? 's' : ''}`;
  }
  if (remainingHours > 0) {
    label += `${label ? ' e ' : ''}${remainingHours} hora${remainingHours > 1 ? 's' : ''}`;
  }
  if (remainingMinutes > 0) {
    label += `${label ? ' e ' : ''}${remainingMinutes} minuto${remainingMinutes > 1 ? 's' : ''}`;
  }

  return `${label} antes`;
}